/**
 * Feature 10 - Security Event Logging.
 *
 * Creates a lightweight audit logger. Every event is console.logged and,
 * when an `apiUrl` is configured, also POSTed (best-effort, non-blocking) to
 * that endpoint. Failures are swallowed so logging can never break the reader.
 */

/** Canonical event names (Feature 10 table). */
export const AuditEvents = {
  DOCUMENT_OPEN: "DocumentOpen",
  FULLSCREEN_ENTER: "FullscreenEnter",
  FULLSCREEN_EXIT: "FullscreenExit",
  WINDOW_BLUR: "WindowBlur",
  WINDOW_FOCUS: "WindowFocus",
  PAGE_HIDDEN: "PageHidden",
  PAGE_VISIBLE: "PageVisible",
  PRINT_ATTEMPT: "PrintAttempt",
  SAVE_ATTEMPT: "SaveAttempt",
  COPY_ATTEMPT: "CopyAttempt",
  CUT_ATTEMPT: "CutAttempt",
  SELECT_ALL_ATTEMPT: "SelectAllAttempt",
  VIEW_SOURCE_ATTEMPT: "ViewSourceAttempt",
  DEVTOOLS_ATTEMPT: "DevToolsAttempt",
  RIGHT_CLICK: "RightClickBlocked",
  TEXT_SELECTION_ATTEMPT: "TextSelectionAttempt",
  DRAG_ATTEMPT: "DragAttemptBlocked",
  SESSION_TIMEOUT: "SessionTimeout",
  WINDOW_MAXIMIZED: "WindowMaximized",
  WINDOW_NOT_MAXIMIZED: "WindowNotMaximized",
  SCREENSHOT_ATTEMPT: "ScreenshotAttempt",

  // --- RShield (per document, configured by the sender on Send / Generate) ---
  RSHIELD_FOCUS_LOSS: "RShieldFocusLoss",
  RSHIELD_ACCESS_REVOKED: "RShieldAccessRevoked",
  RSHIELD_CAPTURE_DETECTED: "RShieldCaptureDetected",
  RSHIELD_NOTIFY_SENDER: "RShieldNotifySender",
  RSHIELD_AWAITING_FULLSCREEN: "RShieldAwaitingFullScreen",
};

export function detectBrowser() {
  try {
    const ua = navigator.userAgent || "";
    if (ua.indexOf("Edg") > -1) return "Edge";
    if (ua.indexOf("OPR") > -1 || ua.indexOf("Opera") > -1) return "Opera";
    if (ua.indexOf("Brave") > -1) return "Brave";
    if (ua.indexOf("Firefox") > -1) return "Firefox";
    if (ua.indexOf("Chrome") > -1) return "Chrome";
    if (ua.indexOf("Safari") > -1) return "Safari";
    return "Unknown";
  } catch (e) {
    return "Unknown";
  }
}

/** Version number for the browser detectBrowser() identified. */
export function detectBrowserVersion() {
  try {
    const ua = navigator.userAgent || "";
    const patterns = [
      /Edg\/([\d.]+)/,
      /OPR\/([\d.]+)/,
      /Firefox\/([\d.]+)/,
      /Chrome\/([\d.]+)/,
      /Version\/([\d.]+).*Safari/,
    ];
    for (let i = 0; i < patterns.length; i++) {
      const match = ua.match(patterns[i]);
      if (match && match[1]) return match[1];
    }
    return "";
  } catch (e) {
    return "";
  }
}

export function detectOS() {
  try {
    const p = (navigator.platform || "") + " " + (navigator.userAgent || "");
    if (/Win/i.test(p)) return "Windows";
    if (/Mac/i.test(p)) return "macOS";
    if (/Android/i.test(p)) return "Android";
    if (/(iPhone|iPad|iPod)/i.test(p)) return "iOS";
    if (/Linux/i.test(p)) return "Linux";
    return "Unknown";
  } catch (e) {
    return "Unknown";
  }
}

/**
 * @param {object} ctx
 * @param {string} ctx.apiUrl   optional POST endpoint ("" = console only)
 * @param {string} ctx.email    reader email
 * @param {string} ctx.documentId
 * @param {string} ctx.sessionId
 * @returns {{ log: (event: string, details?: object) => void }}
 */
export function createAuditLogger(ctx = {}) {
  const { apiUrl = "", email = "", documentId = "", sessionId = "" } = ctx;
  const browser = detectBrowser();
  const operatingSystem = detectOS();

  function log(event, details = {}) {
    try {
      const record = {
        Timestamp: new Date().toISOString(),
        Email: email,
        DocumentId: documentId,
        SessionId: sessionId,
        Event: event,
        Browser: browser,
        OperatingSystem: operatingSystem,
        ...details,
      };

      // Always log locally for audit/monitoring.
      // eslint-disable-next-line no-console
      console.log("[DocProtection][Audit]", record);

      if (apiUrl) {
        // Best-effort, non-blocking. sendBeacon survives page unload.
        const payload = JSON.stringify(record);
        let sent = false;
        if (navigator && typeof navigator.sendBeacon === "function") {
          try {
            const blob = new Blob([payload], { type: "application/json" });
            sent = navigator.sendBeacon(apiUrl, blob);
          } catch (e) {
            sent = false;
          }
        }
        if (!sent && typeof fetch === "function") {
          fetch(apiUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: payload,
            keepalive: true,
          }).catch(() => {});
        }
      }
    } catch (e) {
      // Never throw from the audit logger.
    }
  }

  return { log };
}

export default createAuditLogger;
